import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  PageHeader, DataTable, Panel, Tag, Button, Icon, SearchInput, Segmented, Sheet, ConfirmDialog, exportRows, useCkToast,
  applyClientQuery, distinctValues,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import MemberCardNext from '../members/MemberCardNext.jsx';
import { EXAMINEES_FRAME_LABELS } from './ExamineesDashboard.jsx';
import { useServerTable, sortParams, apiError } from '../../next/ui';

/**
 * נבחנים — new design. Same endpoints as ExamineesList: /american-exams/attempts (server paging, live polling
 * every 5s with "new" rows marked for 15s), an exam picker (one or more exams) + channel filter over the table,
 * member card, deleting an attempt (lets the examinee retake the exam), and the wife feedback call status per attempt.
 */
const POLL_MS = 5000;
const NEW_ROW_MS = 15000;
const ALLOWED_FRAMES = new Set(['daf_yomi', 'shinun', 'meshannim']);
const CHANNELS = [
  { key: 'web', label: 'פורטל', icon: 'link', tone: 'primary' },
  { key: 'ivr', label: 'טלפון', icon: 'phone', tone: 'success' },
  { key: 'import', label: 'ייבוא', icon: 'upload', tone: 'neutral' },
];
const CHANNEL_BY_KEY = Object.fromEntries(CHANNELS.map((c) => [c.key, c]));
// היסטוריה מיובאת — מאיפה הגיע הציון
const IMPORT_SOURCE_LABEL = { yemot: 'ימות', kehilot: 'קהילות' };
const channelLabel = (r) => {
  const c = CHANNEL_BY_KEY[r.channel] || CHANNEL_BY_KEY.web;
  return r.importSource && IMPORT_SOURCE_LABEL[r.importSource] ? `${c.label} — ${IMPORT_SOURCE_LABEL[r.importSource]}` : c.label;
};
const hasFilters = (query) => Object.values(query?.filters || {}).some(Boolean);
const memberDisplayName = (row) => [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';
const scoreText = (r) => (r.maxScore != null ? `${r.score ?? 0}/${r.maxScore}` : String(r.score ?? 0));
const WIFE_CALL_STATES = {
  heard: { label: 'שמעה', tone: 'success' },
  not_heard: { label: 'לא ענתה', tone: 'warning' },
  sent: { label: 'נשלח', tone: 'info' },
  queued: { label: 'בשליחה', tone: 'info' },
  deferred: { label: 'ממתין לסוף השעות השקטות', tone: 'info' },
  failed: { label: 'נכשל', tone: 'danger' },
  skipped: { label: 'לא נשלח', tone: 'neutral' },
  none: { label: 'לא נשלח', tone: 'neutral' },
};
const WIFE_SKIP_REASONS = {
  no_phone_alt: 'אין טלפון אשה',
  invalid_phone: 'טלפון אשה לא תקין',
  disabled: 'האוטומציה כבויה',
  no_template: 'לא נבחרה הקלטה',
  template_not_synced: 'ההקלטה לא סונכרנה לימות',
  no_answers: 'לא ענה על אף שאלה',
  member_not_found: 'החבר לא נמצא',
  no_member: 'החבר לא נמצא',
  not_mishtameret: 'לא במערכת המשתמרת',
};
const wifeCallText = (r) => {
  const w = r.wifeCall || { state: 'none' };
  if (w.state === 'none' && r.channel === 'import') return '';
  const base = (WIFE_CALL_STATES[w.state] || WIFE_CALL_STATES.none).label;
  if (w.state === 'skipped' && w.skipReason) return `${base} — ${WIFE_SKIP_REASONS[w.skipReason] || w.skipReason}`;
  return base;
};
const wifeCallTitle = (w) => [
  w.phone ? `טלפון: ${w.phone}` : '',
  w.heardAt ? `נשמע: ${formatHebrewDateTime(w.heardAt)}${w.heardVia === 'callback' ? ' (חזרה למערכת)' : ''}` : '',
  !w.heardAt && w.lastSentAt && w.state !== 'skipped' ? `נשלח לאחרונה: ${formatHebrewDateTime(w.lastSentAt)}` : '',
  w.sendCount > 1 ? `נשלח ${w.sendCount} פעמים` : '',
  w.error ? `שגיאה: ${w.error}` : '',
].filter(Boolean).join('\n');

const VISIBLE_EXAMS = 8;
// "דף היומי — מבחן 50 — קידושין ז - כ" → heading "מבחן 50", sub "קידושין ז - כ" (the frame prefix is already the page title)
const splitExamTitle = (title, frameLabel) => {
  const parts = String(title || 'מבחן').split(' — ');
  if (parts.length > 1 && parts[0].trim() === frameLabel) parts.shift();
  return { heading: parts[0], sub: parts.slice(1).join(' — ') };
};

function ExamCard({ exam, frameLabel, on, onToggle }) {
  const { heading, sub } = splitExamTitle(exam.title, frameLabel);
  return (
    <button type="button" className={`nx-exam-card ${on ? 'is-on' : ''}`} aria-pressed={on} title={exam.title} onClick={onToggle}>
      <span className="nx-exam-card__check">{on ? <Icon name="check" size={13} /> : null}</span>
      <span className="nx-exam-card__title">{heading}</span>
      {sub ? <span className="nx-exam-card__sub">{sub}</span> : null}
      <span className="nx-exam-card__big"><b>{exam.uniqueExaminees ?? 0}</b> נבחנים</span>
      <span className="nx-exam-card__ch">
        {CHANNELS.map((c) => {
          const n = exam.channels?.[c.key] || 0;
          return (
            <span key={c.key} className={n ? '' : 'is-zero'} title={c.label}>
              <Icon name={c.icon} size={13} />
              {c.label} <b>{n}</b>
            </span>
          );
        })}
      </span>
    </button>
  );
}

/** Exam picker — a card per exam (newest first); click cards to show one or more exams in the table. */
function ExamPickerNext({ frame, examIds, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/american-exams/attempts-dashboard', { params: frame ? { frame } : {} });
      setData(res);
    } catch {
      /* the table still works without the dashboard */
    } finally {
      setLoading(false);
    }
  }, [frame]);
  useEffect(() => { load(); }, [load]);

  const frameLabel = EXAMINEES_FRAME_LABELS[frame];
  // the server returns the exams newest → oldest
  const rows = data?.byExam || [];
  const selectedSet = new Set(examIds);
  const selectedExaminees = rows.filter((r) => selectedSet.has(String(r.examId))).reduce((n, r) => n + (r.uniqueExaminees || 0), 0);
  const toggle = (id) => onChange(selectedSet.has(id) ? examIds.filter((x) => x !== id) : [...examIds, id]);
  const search = q.trim();
  const matching = search ? rows.filter((r) => String(r.title).includes(search)) : rows;
  // collapsed: the newest exams, plus any selected exam further down
  const shown = expanded || search ? matching : matching.filter((r, i) => i < VISIBLE_EXAMS || selectedSet.has(String(r.examId)));
  const hiddenCount = matching.length - shown.length;

  return (
    <Panel
      title="מבחנים"
      sub={[
        frameLabel,
        examIds.length ? `נבחרו ${examIds.length} מבחנים · ${selectedExaminees} נבחנים` : `מוצגים כל המבחנים (${rows.length}) — לחצו על מבחן כדי לסנן`,
      ].filter(Boolean).join(' · ')}
      icon="chart"
      actions={(
        <>
          {examIds.length ? <Button size="sm" variant="ghost" icon="x" onClick={() => onChange([])}>{`ניקוי בחירה (${examIds.length})`}</Button> : null}
          <Button size="sm" variant="ghost" icon="refresh" onClick={load} aria-label="רענון" />
        </>
      )}
    >
      {loading && !data ? <span className="ck-muted">טוען…</span> : (
        <>
          {rows.length > VISIBLE_EXAMS ? (
            <div style={{ maxWidth: 320, marginBottom: 12 }}><SearchInput value={q} onChange={setQ} placeholder="חיפוש מבחן…" /></div>
          ) : null}
          <div className={`nx-exam-grid ${expanded && !search ? 'is-expanded' : ''}`}>
            {shown.map((r) => (
              <ExamCard key={String(r.examId)} exam={r} frameLabel={frameLabel} on={selectedSet.has(String(r.examId))} onToggle={() => toggle(String(r.examId))} />
            ))}
          </div>
          {!rows.length ? <span className="ck-muted">אין נבחנים</span> : null}
          {rows.length && !matching.length ? <span className="ck-muted">לא נמצאו מבחנים</span> : null}
          {!search && (hiddenCount > 0 || expanded) ? (
            <div style={{ marginTop: 10 }}>
              <Button size="sm" variant="ghost" icon={expanded ? 'up' : 'down'} onClick={() => setExpanded((x) => !x)}>
                {expanded ? 'הצג פחות' : `הצג את כל המבחנים (עוד ${hiddenCount})`}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}

export default function ExamineesListNext() {
  const toast = useCkToast();
  const { frame: frameParam } = useParams();
  const frame = String(frameParam || '').trim();
  const frameLabel = EXAMINEES_FRAME_LABELS[frame] || frame;
  const [dash, setDash] = useState({ examIds: [], channel: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newIds, setNewIds] = useState(() => new Set());
  const knownIdsRef = useRef(new Set());
  const initialDoneRef = useRef(false);
  const timersRef = useRef(new Map());
  // The attempts endpoint only searches, so column filters run here over the full list (all=1).
  const columnsRef = useRef([]);
  const allRowsRef = useRef({ sig: '', rows: [] });

  useEffect(() => {
    setDash({ examIds: [], channel: '' });
    knownIdsRef.current = new Set();
    initialDoneRef.current = false;
    setNewIds(new Set());
  }, [frame]);

  const markNewRows = useCallback((rows) => {
    if (!initialDoneRef.current) {
      knownIdsRef.current = new Set(rows.map((r) => String(r._id)));
      initialDoneRef.current = true;
      return;
    }
    const fresh = rows.map((r) => String(r._id)).filter((id) => !knownIdsRef.current.has(id));
    if (!fresh.length) return;
    fresh.forEach((id) => knownIdsRef.current.add(id));
    setNewIds((prev) => new Set([...prev, ...fresh]));
    fresh.forEach((id) => {
      clearTimeout(timersRef.current.get(id));
      timersRef.current.set(id, setTimeout(() => {
        setNewIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        timersRef.current.delete(id);
      }, NEW_ROW_MS));
    });
  }, []);

  const buildParams = useCallback((query, paged = true) => ({
    ...(paged ? { page: query.page, pageSize: query.pageSize } : { all: 1 }),
    ...sortParams(query, 'finishedAt', 'desc'),
    channel: dash.channel,
    frame,
    examId: dash.examIds.join(','),
    search: String(query.search || '').trim(),
  }), [dash, frame]);

  const fetchAllRows = useCallback(async (query) => {
    const params = buildParams(query, false);
    const { data: res } = await api.get('/american-exams/attempts', { params });
    const rows = res.data || [];
    allRowsRef.current = { sig: JSON.stringify(params), rows };
    return rows;
  }, [buildParams]);
  const filterRows = (rows, filters, except) => applyClientQuery(rows, columnsRef.current, { filters }, { except });

  const table = useServerTable(async (query) => {
    if (hasFilters(query)) {
      const all = filterRows(await fetchAllRows(query), query.filters);
      const start = (query.page - 1) * query.pageSize;
      const rows = all.slice(start, start + query.pageSize);
      markNewRows(rows);
      return { rows, total: all.length };
    }
    const { data: res } = await api.get('/american-exams/attempts', { params: buildParams(query) });
    const rows = res.data || [];
    markNewRows(rows);
    return { rows, total: res.total || 0 };
  }, [buildParams, fetchAllRows], { pageSize: 20, sort: { key: 'finishedAt', dir: -1 } });
  const { resetPage, reloadSilent } = table;

  useEffect(() => { resetPage(); }, [dash, frame, resetPage]);

  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') reloadSilent(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    const timers = timersRef.current;
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, [reloadSilent]);

  const exportAll = async () => filterRows(await fetchAllRows(table.query), table.query.filters);

  const getValueOptions = async (colKey, search) => {
    const col = columnsRef.current.find((c) => c.key === colKey);
    if (!col) return [];
    const cached = allRowsRef.current;
    const rows = cached.sig === JSON.stringify(buildParams(table.query, false)) ? cached.rows : await fetchAllRows(table.query);
    return distinctValues(filterRows(rows, table.query.filters, colKey), col, search);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await api.delete(`/american-exams/attempts/${deleteTarget._id}`);
      const id = String(deleteTarget._id);
      table.setRows((prev) => prev.filter((r) => String(r._id) !== id));
      table.setTotal((t) => Math.max(0, (t || 0) - 1));
      knownIdsRef.current.delete(id);
      setDeleteTarget(null);
      toast.success('ניסיון הנבחן נמחק — ניתן להיבחן שוב');
      reloadSilent();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקת הנבחן'));
    } finally {
      setDeleting(false);
    }
  };

  if (!ALLOWED_FRAMES.has(frame)) return <Navigate to="/examinees/daf_yomi" replace />;

  const columns = [
    {
      key: 'finishedAt',
      label: 'תאריך ושעה',
      type: 'date',
      exportValue: (r) => (r.finishedAt ? formatHebrewDateTime(r.finishedAt) : ''),
      render: (r) => (
        <span className="nx-row" style={{ gap: 6 }}>
          {r.finishedAt ? formatHebrewDateTime(r.finishedAt) : '—'}
          {newIds.has(String(r._id)) ? <Tag tone="success" icon="sparkle">חדש</Tag> : null}
        </span>
      ),
    },
    {
      key: '_name',
      label: 'שם',
      sortable: false,
      getValue: memberDisplayName,
      render: (r) => (r.memberId
        ? <button type="button" className="nx-link" onClick={(e) => { e.stopPropagation(); setSelectedId(String(r.memberId)); }}><b>{memberDisplayName(r)}</b></button>
        : memberDisplayName(r)),
    },
    { key: 'idNumber', label: 'ת.ז.', ltr: true, copy: true, sortable: false },
    { key: 'phone', label: 'טלפון', ltr: true, copy: true, sortable: false },
    { key: 'city', label: 'עיר', sortable: false },
    { key: 'occupation', label: 'עיסוק', sortable: false, render: (r) => (r.occupation ? <Tag>{r.occupation}</Tag> : <span className="ck-muted">—</span>) },
    { key: 'examTitle', label: 'מבחן', type: 'enum', sortable: false },
    { key: 'score', label: 'ציון', type: 'number', exportValue: scoreText, render: (r) => <Tag tone={Number(r.score) >= 90 ? 'success' : 'neutral'}><b>{scoreText(r)}</b></Tag> },
    { key: 'correctCount', label: 'נכונות', sortable: false, getValue: (r) => (r.totalQuestions ? `${r.correctCount ?? 0}/${r.totalQuestions}` : '—') },
    {
      key: 'channel',
      label: 'ערוץ',
      type: 'enum',
      sortable: false,
      getValue: (r) => (CHANNEL_BY_KEY[r.channel] ? channelLabel(r) : r.channel || ''),
      render: (r) => { const c = CHANNEL_BY_KEY[r.channel] || CHANNEL_BY_KEY.web; return <Tag tone={c.tone} icon={c.icon}>{channelLabel(r)}</Tag>; },
    },
    {
      key: 'wifeCall',
      label: 'שיחה לאשה',
      type: 'enum',
      sortable: false,
      getValue: wifeCallText,
      render: (r) => {
        const text = wifeCallText(r);
        if (!text) return <span className="ck-muted">—</span>;
        const w = r.wifeCall || { state: 'none' };
        const tone = (WIFE_CALL_STATES[w.state] || WIFE_CALL_STATES.none).tone;
        return <Tag tone={tone} title={wifeCallTitle(w) || undefined}>{text}</Tag>;
      },
    },
  ];
  columnsRef.current = columns;

  return (
    <>
      <PageHeader
        title={`נבחנים — ${frameLabel}`}
        sub="כל מי שסיים מבחן בפורטל או בקו הטלפוני — מתעדכן אוטומטית"
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: table.reload }]}
      />

      <ExamPickerNext key={frame} frame={frame} examIds={dash.examIds} onChange={(examIds) => setDash((d) => ({ ...d, examIds }))} />

      <DataTable
        mode="server"
        {...table.tableProps}
        rowKey="_id"
        columns={columns}
        getValueOptions={getValueOptions}
        storageKey={`bchinot-examinees-${frame}`}
        selectable={false}
        searchPlaceholder="חיפוש לפי שם, ת.ז., טלפון, עיר, עיסוק…"
        exportFilename={`נבחנים-${frameLabel}`}
        onExport={async ({ format, columns: cols }) => {
          exportRows(await exportAll(), cols, { format, filename: `נבחנים-${frameLabel}` });
        }}
        toolbarEnd={(
          <Segmented
            value={dash.channel}
            onChange={(v) => setDash((d) => ({ ...d, channel: v }))}
            items={[{ value: '', label: 'כל הערוצים' }, ...CHANNELS.map((c) => ({ value: c.key, label: c.label }))]}
          />
        )}
        emptyTitle="אין נבחנים להצגה"
        onRowClick={(r) => { if (r.memberId) setSelectedId(String(r.memberId)); }}
        rowActions={(r) => [
          r.memberId ? { key: 'card', icon: 'user', label: 'כרטיס חבר', onClick: () => setSelectedId(String(r.memberId)) } : null,
          { key: 'del', icon: 'trash', label: 'מחיקת הניסיון', onClick: () => setDeleteTarget(r) },
        ].filter(Boolean)}
      />

      <Sheet open={!!selectedId} onClose={() => setSelectedId(null)} width={1080} ariaLabel="כרטיס חבר">
        {selectedId ? <div className="nx-sheet-body"><MemberCardNext key={selectedId} recordId={selectedId} onClose={() => setSelectedId(null)} /></div> : null}
      </Sheet>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
        danger
        title="מחיקת נבחן"
        confirmLabel="מחק"
        message={deleteTarget ? `למחוק את ניסיון הבחינה של «${memberDisplayName(deleteTarget)}» במבחן «${deleteTarget.examTitle || 'מבחן'}»${deleteTarget.maxScore != null ? ` (ציון ${scoreText(deleteTarget)})` : ''}? הפעולה תמחק את הרשומה לצמיתות ותאפשר לנבחן להיבחן שוב על אותו מבחן. לא ניתן לשחזור.` : ''}
      />
    </>
  );
}
